import type {
  DomainEventFrame,
  DomainEventGroupKind,
  DomainEventGroupMember,
} from '#entities/DomainEvent';

export interface CaseTimelineMediaFile {
  path: string;
  type: 'photo' | 'video';
  url?: string;
  posterUrl?: string;
}

export interface CaseTimelineRawEvent {
  seq: string;
  type: string;
  eventId: string;
  occurredAt: string;
  observedAt: string;
  at: string;
  payload: unknown;
  previous?: Record<string, unknown>;
  media?: CaseTimelineMediaFile[];
}

export interface CaseAirLeg {
  legId: string;
  maoTripId: number;
  maoLegId: number;
  departureIcao: string | null;
  arrivalIcao: string | null;
  plannedOffBlocksUtc: string | null;
  plannedOnBlocksUtc: string | null;
  actualTakeOffUtc: string | null;
  actualLandingUtc: string | null;
}

export const APPROVAL_SLOT_KEYS = [
  'serviceAcceptance',
  'aviation',
  'quoteInternal',
  'quoteCustomer',
  'organAcceptance',
] as const;

export type ApprovalSlotKey = (typeof APPROVAL_SLOT_KEYS)[number];

export type ApprovalSlotState =
  'absent' | 'outstanding' | 'approved' | 'declined';

export interface ApprovalSlot {
  key: ApprovalSlotKey;
  state: ApprovalSlotState;
  requestedAt: string | null;
  answeredAt: string | null;
}

export type OrganAcceptanceOutcome = 'accepted' | 'declined';

export interface OrganAcceptanceRound {
  requestedAt: string;
  answeredAt: string | null;
  outcome: OrganAcceptanceOutcome | null;
}

export interface CaseApprovals {
  /** Always five, in `APPROVAL_SLOT_KEYS` order. */
  slots: ApprovalSlot[];
  organRounds: OrganAcceptanceRound[];
}

export interface CaseTimelineCaseData {
  caseId: string;
  caseLabel: string | null;
  frames: DomainEventFrame[];
  events: CaseTimelineRawEvent[];
  airLegs: CaseAirLeg[];
  approvals: CaseApprovals;
}

export interface CasesTimeline {
  latestSeq: string | null;
  cases: CaseTimelineCaseData[];
}

export interface CaseTimelineResult {
  latestSeq: string | null;
  case: CaseTimelineCaseData | null;
}

export interface CasesTimelineArgs {
  sinceSeq?: string;
}

export interface CaseTimelineArgs {
  caseId: string;
}

export type TimelineCheckpointState =
  | 'planned'
  | 'due'
  | 'done'
  | 'slipped'
  | 'unfulfillable'
  | 'superseded'
  | 'cancelled';

export type ProcessOwner =
  | 'Aviation'
  | 'Ground'
  | 'Clinical'
  | 'Device/OCS'
  | 'Finance'
  | 'Planning and approvals'
  | 'Resources'
  | 'Context';

export type RouteLegKind = 'air' | 'ground';

export type OrganAcceptanceCheckpointType =
  'organAcceptanceRequested' | 'organAcceptanceAnswered';

export type TimelineCheckpointType =
  | Exclude<DomainEventGroupKind, 'statusRepeat'>
  | OrganAcceptanceCheckpointType
  | 'airLeg'
  | 'groundLeg';

export const SLIP_TOLERANCED_TYPES = [
  'airLeg',
  'groundLeg',
  'groundStop',
] as const;

export type SlipToleranceType = (typeof SLIP_TOLERANCED_TYPES)[number];

/** Minutes a checkpoint of each type may run past its planned time before it counts as slipped. */
export type SlipTolerance = Readonly<Record<SlipToleranceType, number>>;

export interface TimelineCheckpoint {
  id: string;
  /** A group checkpoint's group key. A leg checkpoint's first merged group key when a group merged into the leg, else its leg id. */
  key?: string;
  type: TimelineCheckpointType;
  owner: ProcessOwner;
  plannedAt?: string;
  actualAt?: string;
  /**
   * `buildCaseTimeline` stores only planned, done and cancelled; superseded is set later.
   * Due, slipped and unfulfillable exist only after `deriveCheckpointState`.
   */
  state: TimelineCheckpointState;
  /** On an answered organ-acceptance checkpoint: how long the answer took after the request. */
  answeredInMs?: number;
  events: readonly DomainEventGroupMember[];
}

export interface StatusRepeatDot {
  id: string;
  occurredAt: string;
  members: readonly DomainEventGroupMember[];
}

export type ClinicalStepLevel = 'organ' | 'donor';

export type ClinicalStepKind = 'value' | 'media' | 'decision';

export interface ClinicalStep {
  /** The raw event's `eventId`. */
  id: string;
  /** The raw event's `seq`; a higher one is a later copy of the same item. */
  seq: string;
  /** The clinical item's identity: its type with `item.id`, else with `item.timestamp`. Sibling cases' copies of one item share it. */
  itemKey: string;
  /** The item's `clinicalItemType`. */
  type: string;
  at: string;
  /** The event's `occurredAt`. It trails `at` for an item entered after the fact. */
  reportedAt: string;
  level: ClinicalStepLevel;
  kind: ClinicalStepKind;
  values: readonly { readonly label: string; readonly value: string }[];
  thumbnailUrl?: string;
}

export interface CaseTimeline {
  caseId: string;
  caseLabel: string | null;
  checkpoints: TimelineCheckpoint[];
  statusRepeats: StatusRepeatDot[];
  approvals: CaseApprovals;
  clinicalSteps: ClinicalStep[];
  organOnOcsAt: string | null;
  /** This case's own Donor in OR item time; sibling cases may know an earlier one. */
  donorInOrAt: string | null;
}

export interface CaseRouteProjectionLeg {
  legId: string;
  kind: RouteLegKind;
  /** The NOP route segment the leg serves, such as `DonorHospitalToTxCenter`. */
  segmentType: string | null;
  departureTime: string | null;
  arrivalTime: string | null;
}

/** The route projection in force: its Main-route air and ground legs. */
export interface CaseRouteProjection {
  seq: string;
  recordedAt: string;
  legs: CaseRouteProjectionLeg[];
}
